<script setup lang="ts">
import { Skeleton } from "@/components/Ui";
import { computed } from "vue";

interface Props {
  itemId: number;
}

const props = defineProps<Props>();
const { t, locale } = useI18n();
const localePath = useLocalePath();
const params = {
  id: props.itemId,
  fields: ["title", "slug", "color"],
  "populate[review_items][fields][0]": "id"
};
const { pending, data } = await useLazyFetch("/api/review-platforms", { params });
const item = computed<any>(() => {
  return data.value?.data;
});

const plurIndex = computed<number>(() => {
  let index = 0;
  if (locale.value === "ru") {
    const length = item.value?.attributes?.review_items?.data?.length;
    const teen = length > 10 && length < 20;
    const endsWithOne = length % 10 === 1;
    if (length === 0) {
      index = 0;
    }
    if (!teen && endsWithOne) {
      return 1;
    }
    if (!teen && length % 10 >= 2 && length % 10 <= 4) {
      return 2;
    }
  }
  return index;
});
</script>

<template>
  <li
    :class="$style.root"
    :style="`background-color: ${item && item?.attributes?.color ? item?.attributes?.color : '#f1f2f4'}`"
  >
    <nuxt-link
      :to="localePath({ path: `/reviews/${item ? item?.attributes?.slug : ''}`})"
      :title="item?.attributes?.title"
    >
      <div :class="$style.body">
        <Skeleton :pending="pending" :options="{ width: 140, height: 133 }">
          <template #loader>
            <circle cx="70" cy="42" r="42" />
            <rect x="0" y="94" rx="8" ry="8" width="140" height="20" />
            <rect x="25" y="118" rx="6" ry="6" width="90" height="15" />
          </template>
          <nuxt-img
            :src="`/img/review-platform/${item?.attributes?.slug}-circle.svg`"
            :class="$style['body-icon']"
            loading="lazy"
          />
          <span>{{ item?.attributes?.title }}</span>
          <span>{{ item?.attributes?.review_items?.data?.length }} {{ t("reviews.count", plurIndex) }}</span>
        </Skeleton>
      </div>
    </nuxt-link>
  </li>
</template>


<style lang="scss" module>
.root {
  @apply rounded-[20px] overflow-hidden transition-all;
  @apply cursor-pointer border border-transparent scale-[1] ease-in-out;

  &:hover {
    @apply scale-[0.96] duration-500;
  }

  & a {
    @apply p-10 block;
  }
}

.body {
  @apply flex flex-col items-center leading-snug;


  &-icon {
    @apply w-[84px] h-[84px] rounded-full;
  }

  & span {
    @apply leading-none;
    &:first-of-type {
      @apply text-xl text-neutral-800 font-medium mt-[10px];
    }

    &:last-of-type {
      @apply text-[15px] text-neutral-400 mt-1;
    }
  }
}
</style>